import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Params, useParams } from 'react-router-dom';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import FetchFailedPage from 'router/subrouters/DataRoom/pages/FetchFailedPage';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import DataRoomDocumentsContext from './DataRoomDocuments.context';
import useAddFolder from '../../hooks/useAddFolder';
import FolderPlusIcon from 'icons/FolderPlus.icon';
import { useTranslation } from 'react-i18next';
import FolderIcon from 'icons/Folder.icon';
import FloatingActionMenu from '../../../FloatingActionMenu';
import UploadCloudIcon from 'icons/UploadCloud.icon';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { DataRoomMember } from 'router/subrouters/DataRoom/models/DataRoomMember';
import EyeIcon from 'icons/Eye.icon';
import useViewAsFiles from '../../hooks/useViewAsFiles';
import CheckToggle from 'components/CheckToggle';
import useMatchMedia from 'hooks/useMatchMedia';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import MembersContext from 'router/subrouters/DataRoom/providers/Members/Members.context';
import { getDataRoomRoute, sortDataRoomNodes } from 'utils';
import useQueryParams from 'hooks/useQueryParams';
import useUpdateIndex from 'hooks/useUpdateIndex';
import useUpdateFailedConversionFile from 'hooks/useUpdateFailedConversionFile';
import useUpdateProcessingFile from 'hooks/useUpdateProcessingFile';
import { DATA_ROOM_INPUT_ID } from 'data/global';
import DataRoomUploadContext from 'router/subrouters/DataRoom/providers/DataRoomUpload/DataRoomUpload.context';
import useCurrentlyDisplayedNodes from '../../hooks/useCurrentlyDisplayedNode';

const DataRoomDocumentsProvider: React.FC = (props) => {
  const { children } = props;

  const { isSuperAdmin } = useContext(CurrentUserContext);
  const { popServerError } = useContext(PopNotificationsContext);
  const {
    isBuySide,
    isAdmin,
    isPublishingAllowed,
    doesntHaveAccess,
    dataRoom: { _id },
    setDataRoom,
  } = useContext(DataRoomContext);
  const { onUploadClick } = useContext(DataRoomUploadContext);
  const { buySideMembers } = useContext(MembersContext);

  const [isLoading, setIsLoading] = useState(true);
  const [isFetchFailed, setIsFetchFailed] = useState(false);
  const [documentRoute, setDocumentRoute] = useState<Array<DataRoomNode>>([]);
  const [areInsightsShown, setAreInsightsShown] = useState(true);
  const [currentlySelectedFolder, setCurrentlySelectedFolder] =
    useState<DataRoomNode>(null);

  const {
    currentlyDisplayedNodes,
    setCurrentlyDisplayedNodes,
    buySidePreviewMember,
    setBuySidePreviewMember,
  } = useCurrentlyDisplayedNodes();

  const { documentId } = useParams<{ documentId: string }>();
  const {
    params: { sort },
  } = useQueryParams<Params>();

  const baseDocsPath = useMemo(
    () => `${getDataRoomRoute(_id, 'documents')}/`,
    [_id],
  );

  const isPhablet = useMatchMedia('isPhablet');

  const { t } = useTranslation();

  const sortCurrentlyDisplayedNodes = useCallback(() => {
    setCurrentlyDisplayedNodes((prev) => sortDataRoomNodes(prev, sort));
  }, [sort, setCurrentlyDisplayedNodes]);

  const { addAFolderModal, AddFolderModal } = useAddFolder({
    id: _id,
    currentlySelectedFolder: currentlySelectedFolder?._id,
    onAddFolderSubmit: (node) => {
      updateBuySidePreviewMember(null);
      setCurrentlyDisplayedNodes((prev) =>
        sortDataRoomNodes([node, ...prev], sort),
      );
    },
  });

  const canChangeView = useMemo(
    () => !isBuySide && isPublishingAllowed && !!buySideMembers?.length,
    [buySideMembers?.length, isBuySide, isPublishingAllowed],
  );

  const canTagFiles = useMemo(
    () => (isSuperAdmin || isAdmin) && !buySidePreviewMember,
    [isAdmin, isSuperAdmin, buySidePreviewMember],
  );

  const isDataRoomEmpty = useMemo(
    () => !currentlyDisplayedNodes?.length && !documentRoute?.length,
    [currentlyDisplayedNodes?.length, documentRoute?.length],
  );

  const baseLevelNodes = useMemo(
    () => (!documentRoute.length ? currentlyDisplayedNodes : []),
    [documentRoute.length, currentlyDisplayedNodes],
  );

  const updateNodeById = useCallback(
    (nodeId: string, newValue: DataRoomNode) => {
      setCurrentlyDisplayedNodes((old) =>
        old.map((oldNode) => (oldNode._id === nodeId ? newValue : oldNode)),
      );
    },
    [setCurrentlyDisplayedNodes],
  );

  const setSelectedFolder = useCallback((node: DataRoomNode) => {
    setCurrentlySelectedFolder(node);

    setDocumentRoute((old) => {
      if (!node) return [];

      const index = old.findIndex(({ name }) => name === node.name);

      if (index === -1) {
        return [...old, node];
      }

      return old.slice(0, index + 1);
    });
  }, []);

  const updateBuySidePreviewMember = useCallback(
    (value: DataRoomMember | null) => setBuySidePreviewMember(value || null),
    [setBuySidePreviewMember],
  );

  const { viewAsModal, openViewAsModal } = useViewAsFiles({
    buySideMembers,
    updateBuySidePreviewMember,
  });

  const menuOptions = useMemo(
    () => [
      ...(canChangeView
        ? [
            {
              icon: <EyeIcon />,
              text: t('viewAs', {
                name:
                  buySidePreviewMember?.fullName || buySidePreviewMember?.email,
              }),
              onClick: () => {
                openViewAsModal();
              },
            },
          ]
        : []),
      ...(isAdmin && !!currentlyDisplayedNodes?.length && !isLoading
        ? [
            {
              text: (
                <div className="show-insights">
                  {t('showInsights')}

                  <CheckToggle
                    checked={areInsightsShown}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              ),
              onClick: () => setAreInsightsShown((prev) => !prev),
            },
          ]
        : []),
      {
        icon: <FolderPlusIcon />,
        text: t('createAFolder'),
        onClick: () => addAFolderModal.open(),
      },
      {
        customHtml: (
          <label
            htmlFor={DATA_ROOM_INPUT_ID}
            className="ncoded-button ncoded-button--outline"
          >
            <FolderIcon />
            {t('uploadAFolder')}
          </label>
        ),
      },
      {
        icon: <UploadCloudIcon />,
        text: t('uploadFiles'),
        onClick: (event: any) => {
          onUploadClick(event);
        },
      },
    ],
    [
      addAFolderModal,
      isLoading,
      areInsightsShown,
      buySidePreviewMember?.email,
      buySidePreviewMember?.fullName,
      canChangeView,
      currentlyDisplayedNodes?.length,
      isAdmin,
      onUploadClick,
      openViewAsModal,
      t,
    ],
  );

  useEffect(() => {
    (async () => {
      try {
        if (documentId) {
          const { data } = await dataRoomApi.nodes.getFolderPath(
            _id,
            documentId,
          );

          setCurrentlySelectedFolder(data[0]);
          setDocumentRoute(data.reverse());
        } else {
          setCurrentlySelectedFolder(null);
          setDocumentRoute([]);
        }
      } catch (e) {
        popServerError(e);
      }
    })();
  }, [_id, documentId, popServerError]);

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        const { data: nodes } = await (buySidePreviewMember
          ? dataRoomApi.nodes.getChildrenForViewAsMembers(
              _id,
              documentId,
              buySidePreviewMember._id,
            )
          : dataRoomApi.nodes.getChildren(_id, documentId));

        setCurrentlyDisplayedNodes(sortDataRoomNodes(nodes, sort));
      } catch (e) {
        popServerError(e);
        setIsFetchFailed(true);
      } finally {
        setIsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    _id,
    documentId,
    buySidePreviewMember,
    popServerError,
    setCurrentlyDisplayedNodes,
  ]);

  useEffect(sortCurrentlyDisplayedNodes, [sort, sortCurrentlyDisplayedNodes]);

  useUpdateIndex(_id, setDataRoom, setCurrentlyDisplayedNodes);

  useUpdateProcessingFile(currentlyDisplayedNodes, setCurrentlyDisplayedNodes);

  useUpdateFailedConversionFile({
    dataRoomId: _id,
    setCurrentlyDisplayedNodes,
  });

  if (isFetchFailed) return <FetchFailedPage />;

  return (
    <DataRoomDocumentsContext.Provider
      value={{
        baseDocsPath,
        areDocsLoading: isLoading,
        documentRoute,
        currentlyDisplayedNodes,
        baseLevelNodes,
        canChangeView,
        buySidePreviewMember,
        addAFolderModal,
        areInsightsShown,
        canTagFiles,
        isDataRoomEmpty,
        setCurrentlySelectedFolder: setSelectedFolder,
        setCurrentlyDisplayedNodes,
        sortCurrentlyDisplayedNodes,
        updateBuySidePreviewMember,
        updateNodeById,
      }}
    >
      {children}
      {AddFolderModal}
      {canChangeView && viewAsModal}
      {!doesntHaveAccess && isPhablet && isPublishingAllowed && (
        <FloatingActionMenu options={menuOptions} />
      )}
    </DataRoomDocumentsContext.Provider>
  );
};

export default DataRoomDocumentsProvider;
